import { Calendar, Clock, Edit2, Users } from 'lucide-react';
import { MeetingTemplate } from '@/types/meeting';

interface TemplateCardProps {
  template: MeetingTemplate;
  onSelect: (template: MeetingTemplate) => void;
  onEdit?: (template: MeetingTemplate) => void;
}

export function TemplateCard({ template, onSelect, onEdit }: TemplateCardProps) {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(new Date(date));
  };

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 flex flex-col"
    >
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
        {onEdit && (
          <button
            onClick={() => onEdit(template)}
            type="button"
            className="p-1 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-50"
            aria-label="Edit template"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex-1">
        <p className="text-sm text-gray-600 mb-4">{template.purpose}</p>

        <div className="space-y-3 mb-4">
          <div className="flex items-center text-sm text-gray-500">
            <Calendar className="w-4 h-4 mr-2" />
            Created: {formatDate(template.createdAt)}
            {template.updatedAt && template.updatedAt > template.createdAt && (
              <span className="ml-2">
                • Modified: {formatDate(template.updatedAt)}
              </span>
            )}
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-2" />
            {template.defaultDuration} minutes
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Users className="w-4 h-4 mr-2" />
            Required: {template.requiredRoles.map(role => 
              role.charAt(0) + role.slice(1).toLowerCase().replace('_', ' ')
            ).join(', ')}
          </div>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900">Default Agenda:</h4>
          <ul className="space-y-1">
            {template.agendaTemplate.map((item, index) => (
              <li key={index} className="text-sm text-gray-600">
                • {item.title} ({item.duration} min)
              </li>
            ))}
          </ul>
        </div>
      </div>

      <button
        onClick={() => onSelect(template)}
        className="mt-6 w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        Use Template
      </button>
    </div>
  );
}